import { useEffect, useRef, useState, type FormEvent } from 'react'
import { KeyRound, Loader2 } from 'lucide-react'
import { useAuth } from '../store/auth'
import { api } from '../lib/api'
import { oidcAvailableInThisClient, oidcStartUrl, takeOidcResult } from '../lib/oidc'
import { Button } from './ui'
import { AppFooter } from './AppFooter'

/**
 * Login (Name + Passwort/PIN). Kindertauglich einfach; Enforcement serverseitig.
 *
 * Zusätzlich — und nur, wenn der Server OIDC konfiguriert hat und wir im Web
 * laufen — ein Knopf „Mit Authentik anmelden" (ADR-082). Der Hauptweg bleibt
 * unverändert: fällt Authentik aus, ist der Knopf weg und alles andere geht
 * weiter wie bisher.
 */
export function LoginScreen(): React.JSX.Element {
  const { login, error } = useAuth()
  const [name, setName] = useState('')
  const [secret, setSecret] = useState('')
  const [busy, setBusy] = useState(false)
  const [ssoEnabled, setSsoEnabled] = useState(false)
  const [ssoError, setSsoError] = useState<string | null>(null)
  const nameRef = useRef<HTMLInputElement>(null)
  const secretRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setSsoError(takeOidcResult().error)
    if (!oidcAvailableInThisClient) return
    // Fehlschlag (Server alt / offline) → Knopf bleibt einfach aus.
    api.oidcStatus().then((s) => setSsoEnabled(s.enabled)).catch(() => setSsoEnabled(false))
  }, [])

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    // Feldwerte direkt aus dem DOM lesen (Ref-Fallback): iOS-Paste/AutoFill aus
    // einem Passwort-Manager aktualisiert den React-State im WKWebView nicht immer
    // zuverlässig — der Feldinhalt ist die verlässliche Quelle. (Name getrimmt,
    // Passwort NICHT — Leerzeichen im Geheimnis bleiben erhalten.)
    const n = (nameRef.current?.value ?? name).trim()
    const s = secretRef.current?.value ?? secret
    setBusy(true)
    await login(n, s)
    setBusy(false)
  }

  return (
    <main className="h-full overflow-y-auto">
      <div className="flex min-h-full flex-col">
        <div className="mx-auto my-auto w-full max-w-sm p-6">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <img src="/logo2.png" alt="FamilyNest" className="w-72" />
          <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
            Unser Zuhause. Unsere Zeit. Unsere Familie.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="flex flex-col gap-4 rounded-[var(--radius-card)] border p-6"
          style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}
        >
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--color-muted)' }}>Name</span>
            <input
              ref={nameRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="rounded-lg border px-3 py-2 outline-none focus:border-[var(--color-accent)]"
              style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' }}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--color-muted)' }}>Passwort / PIN</span>
            <input
              ref={secretRef}
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              autoComplete="current-password"
              className="rounded-lg border px-3 py-2 outline-none focus:border-[var(--color-accent)]"
              style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' }}
            />
          </label>

          {(error || ssoError) && (
            <p className="text-sm" style={{ color: 'var(--color-danger)' }} role="alert">
              {error ?? ssoError}
            </p>
          )}

          <Button type="submit" disabled={busy}>
            {busy && <Loader2 className="size-4 animate-spin" />}
            Anmelden
          </Button>

          {ssoEnabled && (
            <>
              <div className="flex items-center gap-3" aria-hidden="true">
                <span className="h-px flex-1" style={{ backgroundColor: 'var(--color-line)' }} />
                <span className="text-xs" style={{ color: 'var(--color-muted)' }}>oder</span>
                <span className="h-px flex-1" style={{ backgroundColor: 'var(--color-line)' }} />
              </div>
              <Button
                type="button"
                variant="ghost"
                className="border"
                style={{ color: 'var(--color-ink)', borderColor: 'var(--color-line)' }}
                onClick={() => { window.location.href = oidcStartUrl('login') }}
              >
                <KeyRound className="size-4" />
                Mit Authentik anmelden
              </Button>
            </>
          )}
        </form>
        <AppFooter />
        </div>
      </div>
    </main>
  )
}
